import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock, AlertTriangle, Settings as SettingsIcon, Pencil, Plus, Trash2,
  GripVertical, Maximize2, Minimize2, Camera as CameraIcon, DoorOpen,
  Bell, Monitor, ShieldCheck, Sun, RefreshCw, ChevronRight, Save, HelpCircle
} from 'lucide-react';
import useTutorialStore from '../stores/tutorialStore';
import useTutorialPrompt from '../hooks/useTutorialPrompt';
import TutorialPrompt from '../components/tutorials/TutorialPrompt';
import liveViewTutorial from '../components/tutorials/configs/liveViewTutorial';
import useAuthStore from '../stores/authStore';
import useDashboardStore from '../stores/dashboardStore';
import fetchWithTenant from '../utils/fetchWithTenant';
import LazyThumbnail from '../components/LazyThumbnail';
import ShabbatModeButton from '../components/ShabbatModeButton';
import WidgetSettingsModal from '../components/WidgetSettingsModal';
import SplunkWidget from '../components/SplunkWidget';
import StatSummaryRow from '../components/StatSummaryRow';
import LockdownConfirmModal from '../components/LockdownConfirmModal';
import useLockdown from '../hooks/useLockdown';
import useIsMobile from '../hooks/useIsMobile';
import MobileHome from '../components/MobileHome';

// ── Widget registry ───────────────────────────────────────────
const WIDGET_META = {
  cameras:   { title: 'Cameras',          icon: CameraIcon,  viewAll: '/cameras',           hasSettings: true },
  doors:     { title: 'Recent Door Activity', icon: DoorOpen, viewAll: '/access?tab=live',   hasSettings: true },
  alerts:    { title: 'Active Alerts',    icon: Bell,        viewAll: '/monitoring',        hasSettings: true },
  devices:   { title: 'Monitored Devices', icon: Monitor,    viewAll: '/monitoring/devices', hasSettings: true },
  recording: { title: 'Recording Assurance', icon: ShieldCheck, viewAll: '/recording-assurance', hasSettings: false },
  sabbath:   { title: 'Sabbath Mode',     icon: Sun,         viewAll: '/shabbat',           hasSettings: false },
};

const ALL_WIDGET_IDS = Object.keys(WIDGET_META);

// ── Greeting ──────────────────────────────────────────────────
function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}

// ── Dashboard Header ──────────────────────────────────────────
function DashboardHeader({ user, tenantName, editing, onToggleEdit, onHelp }) {
  const greeting = getGreeting();
  const firstName = (user?.name || user?.username || '').split(' ')[0] || '';
  return (
    <div className="dashboard-header">
      <div className="dashboard-header-text">
        <h1 className="dashboard-greeting">{greeting}{firstName ? `, ${firstName}` : ''}</h1>
        {tenantName && <p className="dashboard-tenant-name">{tenantName}</p>}
      </div>
      <div className="dashboard-header-actions">
        <button className="tutorial-help-btn" onClick={onHelp} title="Take a tour" aria-label="Take a tour">
          <HelpCircle size={16} />
        </button>
        <button
          className={`dashboard-customize-btn ${editing ? 'active' : ''}`}
          onClick={onToggleEdit}
        >
          {editing ? <><Save size={16} /> Done</> : <><Pencil size={16} /> Customize</>}
        </button>
      </div>
    </div>
  );
}

// StatSummaryRow moved to src/components/StatSummaryRow.jsx (shared with MobileHome).

// ── Widget shell ──────────────────────────────────────────────
function WidgetShell({ widget, index, editing, onRemove, onToggleSize, onOpenSettings, dragHandlers, isDragOver, children, error, onRetry }) {
  const meta = WIDGET_META[widget.id];
  if (!meta) return null;
  const Icon = meta.icon;
  const navigate = useNavigate();

  return (
    <div
      className={`dashboard-widget size-${widget.size} ${editing ? 'editing' : ''} ${isDragOver ? 'drag-over' : ''}`}
      {...dragHandlers}
    >
      <div className="dashboard-widget-header">
        <div className="dashboard-widget-title">
          {editing && <GripVertical size={16} className="dashboard-widget-drag" />}
          <Icon size={16} />
          <span>{meta.title}</span>
        </div>
        <div className="dashboard-widget-actions">
          {editing ? (
            <>
              <button className="dashboard-widget-btn" onClick={() => onToggleSize(index)} title={widget.size === 'full' ? 'Make half width' : 'Make full width'}>
                {widget.size === 'full' ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
              </button>
              {meta.hasSettings && (
                <button className="dashboard-widget-btn" onClick={() => onOpenSettings(index)} title="Settings">
                  <SettingsIcon size={14} />
                </button>
              )}
              <button className="dashboard-widget-btn danger" onClick={() => onRemove(index)} title="Remove">
                <Trash2 size={14} />
              </button>
            </>
          ) : (
            <button className="dashboard-widget-viewall" onClick={() => navigate(meta.viewAll)}>
              View all <ChevronRight size={14} />
            </button>
          )}
        </div>
      </div>
      <div className="dashboard-widget-body">
        {error ? (
          <div className="dashboard-widget-error">
            <AlertTriangle size={20} />
            <p>Failed to load</p>
            {onRetry && <button className="dashboard-widget-retry" onClick={onRetry}><RefreshCw size={14} /> Retry</button>}
          </div>
        ) : children}
      </div>
    </div>
  );
}

// ── Cameras Widget ────────────────────────────────────────────
function CamerasWidget({ widget, index, tenantReady, tenantVersion, ...shellProps }) {
  const navigate = useNavigate();
  const [cameras, setCameras] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!tenantReady) return;
    setError(false);
    try {
      const r = await fetchWithTenant('/api/vms/cameras');
      if (!r.ok) throw new Error('fetch failed');
      const d = await r.json();
      const list = Array.isArray(d) ? d : (d.cameras || []);
      setCameras(list);
    } catch {
      setError(true);
    }
  }, [tenantReady, tenantVersion]);

  useEffect(() => { load(); }, [load]);

  const ids = widget.settings?.cameraIds || [];
  const filtered = (cameras || []).filter(c => !ids.length || ids.includes(c.id));
  const display = filtered.slice(0, 6);
  const cols = display.length >= 5 ? 3 : 2;

  return (
    <WidgetShell widget={widget} index={index} {...shellProps} error={error} onRetry={load}>
      {!cameras ? (
        <div className="dashboard-widget-skeleton" />
      ) : display.length === 0 ? (
        <div className="dashboard-widget-empty">No cameras</div>
      ) : (
        <div className={`widget-cam-grid cols-${cols}`}>
          {display.map(cam => (
            <div
              key={cam.id}
              className="widget-cam-thumb"
              onClick={() => navigate(`/cameras?camera=${cam.id}`)}
            >
              <LazyThumbnail cameraId={cam.id} alt={cam.name} />
              <div className="widget-cam-name">{cam.name}</div>
            </div>
          ))}
        </div>
      )}
    </WidgetShell>
  );
}

// ── Doors Widget ──────────────────────────────────────────────
function DoorsWidget({ widget, index, tenantReady, tenantVersion, ...shellProps }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!tenantReady) return;
    setError(false);
    try {
      const r = await fetchWithTenant('/api/acs/events');
      if (!r.ok) throw new Error('fetch failed');
      const d = await r.json();
      const list = Array.isArray(d) ? d : (d.events || []);
      setEvents(list);
    } catch {
      setError(true);
    }
  }, [tenantReady, tenantVersion]);

  useEffect(() => { load(); }, [load]);

  const ids = widget.settings?.doorIds || [];
  const filtered = (events || []).filter(e => !ids.length || ids.includes(e.doorId || e.readerId));
  const display = filtered.slice(0, 5);

  return (
    <WidgetShell widget={widget} index={index} {...shellProps} error={error} onRetry={load}>
      {!events ? (
        <div className="dashboard-widget-skeleton" />
      ) : display.length === 0 ? (
        <div className="dashboard-widget-empty">No recent events</div>
      ) : (
        <ul className="widget-doors-list">
          {display.map((e, i) => {
            const granted = e.granted !== false && e.accessGranted !== false && !/denied/i.test(e.eventType || '');
            const time = new Date(e.timestamp || e.eventTime || e.createdAt || Date.now());
            const doorId = e.doorId || e.readerId;
            const target = doorId
              ? `/access?tab=live&door=${encodeURIComponent(doorId)}`
              : '/access?tab=live';
            return (
              <li key={e.id || i} className="widget-doors-item" onClick={() => navigate(target)}>
                <div className="widget-doors-main">
                  <div className="widget-doors-name">{e.personName || e.cardholderName || 'Unknown'}</div>
                  <div className="widget-doors-door">{e.doorName || e.readerName || '—'}</div>
                </div>
                <span className={`widget-doors-badge ${granted ? 'granted' : 'denied'}`}>
                  {granted ? 'Granted' : 'Denied'}
                </span>
                <div className="widget-doors-time">
                  {isNaN(time) ? '' : time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetShell>
  );
}

// ── Alerts Widget ─────────────────────────────────────────────
function AlertsWidget({ widget, index, tenantReady, tenantVersion, ...shellProps }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState(null);
  const [error, setError] = useState(false);
  const limit = widget.settings?.limit || 5;
  const severityFilter = widget.settings?.severities || [];

  const load = useCallback(async () => {
    if (!tenantReady) return;
    setError(false);
    try {
      const r = await fetchWithTenant(`/api/md/alerts?status=active&limit=${limit}`);
      if (!r.ok) throw new Error('fetch failed');
      const d = await r.json();
      const list = Array.isArray(d) ? d : (d.alerts || []);
      setAlerts(list);
    } catch {
      setError(true);
    }
  }, [limit, tenantReady, tenantVersion]);

  useEffect(() => { load(); }, [load]);

  const filtered = (alerts || []).filter(a => !severityFilter.length || severityFilter.includes(String(a.severity || '').toLowerCase()));
  const display = filtered.slice(0, limit);

  return (
    <WidgetShell widget={widget} index={index} {...shellProps} error={error} onRetry={load}>
      {!alerts ? (
        <div className="dashboard-widget-skeleton" />
      ) : display.length === 0 ? (
        <div className="dashboard-widget-empty">No active alerts</div>
      ) : (
        <ul className="widget-alerts-list">
          {display.map(a => {
            const sev = String(a.severity || 'info').toLowerCase();
            // Prefer device detail if the alert has a device association,
            // otherwise land on the monitoring dashboard (no per-alert page exists).
            const target = a.deviceId
              ? `/monitoring/devices/${a.deviceId}`
              : '/monitoring';
            return (
              <li key={a.id} className={`widget-alerts-item sev-${sev}`} onClick={() => navigate(target)}>
                <span className={`widget-alerts-dot sev-${sev}`} />
                <div className="widget-alerts-main">
                  <div className="widget-alerts-msg">{a.message || a.title || 'Alert'}</div>
                  <div className="widget-alerts-meta">{a.deviceName || a.source || ''}</div>
                </div>
                <span className={`widget-alerts-badge sev-${sev}`}>{sev}</span>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetShell>
  );
}

// ── Devices Widget ────────────────────────────────────────────
function DevicesWidget({ widget, index, tenantReady, tenantVersion, ...shellProps }) {
  const navigate = useNavigate();
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!tenantReady) return;
    setError(false);
    try {
      const r = await fetchWithTenant('/api/md/devices');
      if (!r.ok) throw new Error('fetch failed');
      const d = await r.json();
      const list = Array.isArray(d) ? d : (d.devices || []);
      setDevices(list);
    } catch {
      setError(true);
    }
  }, [tenantReady, tenantVersion]);

  useEffect(() => { load(); }, [load]);

  const ids = widget.settings?.deviceIds || [];
  const filter = widget.settings?.filter || 'all';

  let filtered = (devices || []);
  if (ids.length) filtered = filtered.filter(d => ids.includes(d.id));
  if (filter === 'online') {
    filtered = filtered.filter(d => String(d.status || '').toLowerCase() === 'online' || String(d.healthStatus || '').toLowerCase() === 'online');
  } else if (filter === 'issues') {
    filtered = filtered.filter(d => {
      const s = String(d.status || d.healthStatus || '').toLowerCase();
      return s && s !== 'online' && s !== 'ok';
    });
  }
  const display = filtered.slice(0, 6);

  return (
    <WidgetShell widget={widget} index={index} {...shellProps} error={error} onRetry={load}>
      {!devices ? (
        <div className="dashboard-widget-skeleton" />
      ) : display.length === 0 ? (
        <div className="dashboard-widget-empty">No devices</div>
      ) : (
        <ul className="widget-devices-list">
          {display.map(d => {
            const status = String(d.status || d.healthStatus || 'unknown').toLowerCase();
            const ok = status === 'online' || status === 'ok';
            return (
              <li key={d.id} className="widget-devices-item" onClick={() => navigate(`/monitoring/devices/${d.id}`)}>
                <span className={`widget-devices-dot ${ok ? 'ok' : 'fail'}`} />
                <div className="widget-devices-main">
                  <div className="widget-devices-name">{d.name || d.hostname || 'Device'}</div>
                  <div className="widget-devices-meta">{d.deviceType || d.type || ''}</div>
                </div>
                <span className={`widget-devices-status ${ok ? 'ok' : 'fail'}`}>{status}</span>
              </li>
            );
          })}
        </ul>
      )}
    </WidgetShell>
  );
}

// ── Recording Widget ──────────────────────────────────────────
function RecordingWidget({ widget, index, tenantReady, tenantVersion, ...shellProps }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!tenantReady) return;
    setError(false);
    try {
      const r = await fetchWithTenant('/api/recording-assurance/dashboard');
      if (!r.ok) throw new Error('fetch failed');
      const d = await r.json();
      setData(d);
    } catch {
      setError(true);
    }
  }, [tenantReady, tenantVersion]);

  useEffect(() => { load(); }, [load]);

  if (!data && !error) {
    return (
      <WidgetShell widget={widget} index={index} {...shellProps}>
        <div className="dashboard-widget-skeleton" />
      </WidgetShell>
    );
  }

  const verified = data?.cameras?.verified ?? data?.verified ?? 0;
  const degraded = data?.cameras?.degraded ?? data?.degraded ?? 0;
  const failed = data?.cameras?.failed ?? data?.failed ?? 0;
  const confidence = data?.avgConfidence ?? data?.confidence ?? 0;
  const serviceStatus = data?.overallStatus || data?.serviceStatus || 'unknown';
  const lastChecked = data?.lastChecked || data?.checkedAt;
  const gapCount = data?.gapCount ?? data?.gaps;

  return (
    <WidgetShell widget={widget} index={index} {...shellProps} error={error} onRetry={load}>
      <div className="widget-recording" onClick={() => navigate('/recording-assurance')}>
        <div className="widget-recording-gauge">
          <div className="widget-recording-confidence">{Math.round(confidence)}%</div>
          <div className="widget-recording-confidence-label">Confidence</div>
        </div>
        <div className="widget-recording-stats">
          <div className="widget-recording-stat ok">
            <span className="widget-recording-num">{verified}</span>
            <span className="widget-recording-lbl">Verified</span>
          </div>
          <div className="widget-recording-stat warn">
            <span className="widget-recording-num">{degraded}</span>
            <span className="widget-recording-lbl">Degraded</span>
          </div>
          <div className="widget-recording-stat fail">
            <span className="widget-recording-num">{failed}</span>
            <span className="widget-recording-lbl">Failed</span>
          </div>
        </div>
        <div className="widget-recording-meta">
          <span className={`widget-recording-svc ${String(serviceStatus).toLowerCase()}`}>
            Service: {serviceStatus}
          </span>
          {gapCount != null && <span className="widget-recording-gaps">{gapCount} gaps</span>}
          {lastChecked && (
            <span className="widget-recording-checked">
              Checked {new Date(lastChecked).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </div>
      </div>
    </WidgetShell>
  );
}

// ── Sabbath Widget ────────────────────────────────────────────
function SabbathWidget({ widget, index, tenantReady, tenantVersion, ...shellProps }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!tenantReady) return;
    setError(false);
    try {
      const r = await fetchWithTenant('/api/sabbath/status');
      if (!r.ok) throw new Error('fetch failed');
      setStatus(await r.json());
    } catch {
      setError(true);
    }
  }, [tenantReady, tenantVersion]);

  useEffect(() => { load(); }, [load]);

  return (
    <WidgetShell widget={widget} index={index} {...shellProps} error={error} onRetry={load}>
      {!status ? (
        <div className="dashboard-widget-skeleton" />
      ) : (
        <div className="widget-sabbath" onClick={() => navigate('/shabbat')}>
          <div className="widget-sabbath-name">{status.scheduleName || 'Sabbath Mode'}</div>
          <span className={`widget-sabbath-badge ${status.active ? 'active' : 'inactive'}`}>
            {status.active ? 'Active' : 'Inactive'}
          </span>
          {status.nextStart && (
            <div className="widget-sabbath-next">
              Next: {new Date(status.nextStart).toLocaleString()}
            </div>
          )}
        </div>
      )}
    </WidgetShell>
  );
}

// ── Widget Grid ───────────────────────────────────────────────
function WidgetGrid({ config, editing, onRemove, onToggleSize, onSwap, onOpenSettings, tenantReady, tenantVersion }) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const buildHandlers = (index) => editing ? {
    draggable: true,
    onDragStart: (e) => {
      setDraggedIndex(index);
      e.dataTransfer.effectAllowed = 'move';
    },
    onDragOver: (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (dragOverIndex !== index) setDragOverIndex(index);
    },
    onDragLeave: () => {
      if (dragOverIndex === index) setDragOverIndex(null);
    },
    onDrop: (e) => {
      e.preventDefault();
      if (draggedIndex !== null && draggedIndex !== index) {
        onSwap(draggedIndex, index);
      }
      setDraggedIndex(null);
      setDragOverIndex(null);
    },
    onDragEnd: () => {
      setDraggedIndex(null);
      setDragOverIndex(null);
    },
  } : {};

  const renderWidget = (w, i) => {
    const props = {
      widget: w,
      index: i,
      editing,
      onRemove,
      onToggleSize,
      onOpenSettings,
      tenantReady,
      tenantVersion,
      dragHandlers: buildHandlers(i),
      isDragOver: dragOverIndex === i && draggedIndex !== i,
    };
    switch (w.id) {
      case 'cameras':   return <CamerasWidget key={`${w.id}-${i}`} {...props} />;
      case 'doors':     return <DoorsWidget key={`${w.id}-${i}`} {...props} />;
      case 'alerts':    return <AlertsWidget key={`${w.id}-${i}`} {...props} />;
      case 'devices':   return <DevicesWidget key={`${w.id}-${i}`} {...props} />;
      case 'recording': return <RecordingWidget key={`${w.id}-${i}`} {...props} />;
      case 'sabbath':   return <SabbathWidget key={`${w.id}-${i}`} {...props} />;
      default: return null;
    }
  };

  return (
    <div className="dashboard-widget-grid">
      {config.map(renderWidget)}
    </div>
  );
}

// ── Add Widget Panel ──────────────────────────────────────────
function AddWidgetPanel({ existingIds, onAdd }) {
  const available = ALL_WIDGET_IDS.filter(id => !existingIds.includes(id));
  if (available.length === 0) return null;

  return (
    <div className="dashboard-add-panel">
      <div className="dashboard-add-title">Add Widget</div>
      <div className="dashboard-add-list">
        {available.map(id => {
          const meta = WIDGET_META[id];
          const Icon = meta.icon;
          return (
            <button key={id} className="dashboard-add-btn" onClick={() => onAdd(id)}>
              <Icon size={16} /> {meta.title} <Plus size={14} />
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Home Page ────────────────────────────────────────────
const Home = () => {
  const { user, tenantVersion, currentTenant } = useAuthStore();
  const {
    config, loaded, editing,
    loadConfig, setEditing, removeWidget, toggleSize, swapWidgets, addWidget, updateWidgetSettings,
  } = useDashboardStore();

  const [settingsIndex, setSettingsIndex] = useState(null);
  const isMobile = useIsMobile();

  // Module flags from tenant context
  const modules = currentTenant?.modules || {};
  const hasAcs = modules.hasAcs || false;
  const hasShabbatConfig = modules.hasShabbatConfig || false;
  const shabbatEnabled = modules.shabbatEnabled || false;
  const canLockdown = user?.canLockdown || false;

  // Shared emergency-lockdown state/handler (also used by MobileHome).
  const {
    isLockedDown, showLockdownConfirm, setShowLockdownConfirm, lockdownLoading, handleLockdown,
  } = useLockdown({ hasAcs, tenantVersion });

  // Gate widget fetches until the tenant context is ready.
  // On first login, currentTenant may be null or tenantVersion may be 0
  // before switchTenant completes — widgets firing before that hit the
  // server without an x-tenant-id header and fail.
  const tenantReady = Boolean(currentTenant?.id) && tenantVersion > 0;

  // Load dashboard config on mount
  useEffect(() => { loadConfig(); }, [loadConfig]);

  const existingIds = config.map(w => w.id);

  const startTutorial = useTutorialStore((s) => s.startTutorial);
  const { showPrompt, startTutorial: startPrompt, dismissTutorial } = useTutorialPrompt(liveViewTutorial);

  // On narrow viewports render the simplified mobile home instead of the
  // modular dashboard. All hooks above run unconditionally (rules of hooks);
  // desktop rendering below is unchanged.
  if (isMobile) return <MobileHome />;

  return (
    <div className="dashboard-page">
      <DashboardHeader
        user={user}
        tenantName={currentTenant?.name}
        editing={editing}
        onToggleEdit={() => setEditing(!editing)}
        onHelp={() => startTutorial(liveViewTutorial.module, liveViewTutorial.steps)}
      />

      {/* Lockdown bar (preserved) */}
      {canLockdown && hasAcs && (
        <div className="lockdown-container">
          <div className={`lockdown-box ${isLockedDown ? 'active' : ''}`}>
            <div className="lockdown-content">
              <AlertTriangle size={24} />
              <div className="lockdown-text">
                <h3>Emergency Lockdown</h3>
                <p>{isLockedDown ? 'System is currently in lockdown mode' : 'Lock all doors immediately in case of emergency'}</p>
              </div>
            </div>
            <button
              className={`btn lockdown-btn ${isLockedDown ? 'btn-success' : 'btn-danger'}`}
              onClick={() => setShowLockdownConfirm(true)}
              disabled={lockdownLoading}
            >
              <Lock size={18} />
              {isLockedDown ? 'Release Lockdown' : 'Initiate Lockdown'}
            </button>
          </div>
        </div>
      )}

      {/* Sabbath bar (preserved) */}
      {hasShabbatConfig && (user?.isPlatformAdmin || (shabbatEnabled && user?.canActivateShabbat)) && (
        <ShabbatModeButton />
      )}

      <StatSummaryRow tenantReady={tenantReady} tenantVersion={tenantVersion} />

      {tenantReady && <SplunkWidget />}

      {loaded && (
        <WidgetGrid
          config={config}
          editing={editing}
          onRemove={removeWidget}
          onToggleSize={toggleSize}
          onSwap={swapWidgets}
          onOpenSettings={(i) => setSettingsIndex(i)}
          tenantReady={tenantReady}
          tenantVersion={tenantVersion}
        />
      )}

      {editing && (
        <AddWidgetPanel existingIds={existingIds} onAdd={(id) => addWidget(id, 'half')} />
      )}

      {/* Widget settings modal */}
      {settingsIndex !== null && config[settingsIndex] && (
        <WidgetSettingsModal
          widget={config[settingsIndex]}
          onSave={(settings) => {
            updateWidgetSettings(settingsIndex, settings);
            setSettingsIndex(null);
          }}
          onClose={() => setSettingsIndex(null)}
        />
      )}

      {/* Lockdown Confirmation Modal (shared with MobileHome) */}
      <LockdownConfirmModal
        show={showLockdownConfirm}
        isLockedDown={isLockedDown}
        loading={lockdownLoading}
        onConfirm={handleLockdown}
        onCancel={() => setShowLockdownConfirm(false)}
      />

      {showPrompt && (
        <TutorialPrompt
          module={liveViewTutorial.module}
          moduleName={liveViewTutorial.moduleName}
          steps={liveViewTutorial.steps}
          onClose={dismissTutorial}
        />
      )}
    </div>
  );
};

export default Home;
